import { confirmar } from '@/components/ui/confirm-dialog';
import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Trash2,
  Loader2,
  Save,
  Pencil,
  X,
  History,
  Weight,
  Box,
  DollarSign,
  Ship,
  Plane,
  Truck,
  TrainFront,
  Container as ContainerIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/lib/hooks/use-toast';
import {
  freightLegPricingsService,
  type FreightRouteLegProviderPricing,
  type LegPricingsBundle,
  type CreateLegPricingDto,
} from '../services/freight-leg-pricings-service';
import { freightProviderService, type FreightProvider } from '../services/freight-provider-service';
import { masterDataService } from '../services/master-data-service';
import type { Currency } from '../types/master-data.types';

// ── Constantes ──────────────────────────────────────────────────────
// Fallback si el endpoint de divisas falla — solo se usa si no se
// pudieron cargar las del tenant.
const FALLBACK_CURRENCIES = ['USD', 'BOB', 'EUR'];

const MODE_ICON: Record<string, React.ElementType> = {
  SEA: Ship,
  AIR: Plane,
  LAND: Truck,
  RAIL: TrainFront,
  MULTIMODAL: ContainerIcon,
};

const MODE_LABEL: Record<string, string> = {
  SEA: 'Marítimo',
  AIR: 'Aéreo',
  LAND: 'Terrestre',
  RAIL: 'Ferroviario',
  MULTIMODAL: 'Multimodal',
};

const LEG_TYPE_LABEL: Record<string, string> = {
  INLAND_ORIGIN: 'Tramo origen interno',
  INTERNATIONAL: 'Tramo internacional',
  INLAND_DESTINATION: 'Tramo destino interno',
  HANDLING: 'Manipuleo',
};

// ── Helpers ─────────────────────────────────────────────────────────
function asNumber(v: number | string | null | undefined): number {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
}

function fmt(date: string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('es-BO', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
}

// ── Props ───────────────────────────────────────────────────────────
interface Props {
  legId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

interface FormState {
  providerId: string;
  // Tres inputs separados — el usuario llena UNO según cómo cobra el proveedor.
  // El sistema infiere pricingType + unitPrice del que tenga valor.
  ratePerKg: string;
  ratePerCbm: string;
  flatAmount: string;
  currency: string;
  minimumCharge: string;
  estimatedDays: string;
  notes: string;
}

const EMPTY_FORM: FormState = {
  providerId: '',
  ratePerKg: '',
  ratePerCbm: '',
  flatAmount: '',
  currency: 'USD',
  minimumCharge: '',
  estimatedDays: '',
  notes: '',
};

// ── Componente ──────────────────────────────────────────────────────
export function LegCatalogPricingDialog({ legId, open, onOpenChange, onSaved }: Props) {
  const { toast } = useToast();
  const [bundle, setBundle] = useState<LegPricingsBundle | null>(null);
  const [providers, setProviders] = useState<FreightProvider[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedProviderId, setExpandedProviderId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [bundleData, providersData, currenciesData] = await Promise.all([
        freightLegPricingsService.getByLeg(legId),
        freightProviderService.getAll(),
        masterDataService.getCurrencies().catch(() => []),
      ]);
      setBundle(bundleData);
      setProviders(providersData);
      setCurrencies(currenciesData.filter((c) => c.isActive));
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.message ?? 'No se pudo cargar',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [legId, toast]);

  useEffect(() => {
    if (!open) return;
    loadData();
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowAddForm(false);
    setExpandedProviderId(null);
  }, [open, loadData]);

  // ── Acciones ───────────────────────────────────────────────────
  const startAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowAddForm(true);
  };

  const startEdit = (p: FreightRouteLegProviderPricing) => {
    setEditingId(p.id);
    setShowAddForm(false);
    setForm({
      providerId: p.providerId,
      ratePerKg: p.ratePerKg != null ? String(p.ratePerKg) : '',
      ratePerCbm: p.ratePerCbm != null ? String(p.ratePerCbm) : '',
      flatAmount: p.flatAmount != null ? String(p.flatAmount) : '',
      currency: p.currency || 'USD',
      minimumCharge: p.minimumCharge != null ? String(p.minimumCharge) : '',
      estimatedDays: p.estimatedDays != null ? String(p.estimatedDays) : '',
      notes: p.notes ?? '',
    });
  };

  const cancelForm = () => {
    setEditingId(null);
    setShowAddForm(false);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.providerId) {
      toast({ title: 'Falta proveedor', variant: 'destructive' });
      return;
    }

    // Parseamos las tres tarifas (todas opcionales). Validación: al menos
    // una debe ser > 0. El usuario puede cargar varias y al cotizar elige.
    const parseRate = (v: string): number | null => {
      if (!v) return null;
      const n = parseFloat(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    };
    const ratePerKg = parseRate(form.ratePerKg);
    const ratePerCbm = parseRate(form.ratePerCbm);
    const flatAmount = parseRate(form.flatAmount);

    if (ratePerKg == null && ratePerCbm == null && flatAmount == null) {
      toast({
        title: 'Falta tarifa',
        description: 'Cargá al menos una tarifa (Kg, m³ o monto fijo)',
        variant: 'destructive',
      });
      return;
    }

    const minCharge = form.minimumCharge ? parseFloat(form.minimumCharge) : null;
    const days = form.estimatedDays ? parseInt(form.estimatedDays, 10) : null;

    setSaving(true);
    try {
      if (editingId) {
        await freightLegPricingsService.update(editingId, {
          ratePerKg,
          ratePerCbm,
          flatAmount,
          currency: form.currency,
          minimumCharge: minCharge,
          estimatedDays: days,
          notes: form.notes || null,
        });
        toast({ title: 'Precio actualizado · nueva versión creada', variant: 'success' });
      } else {
        const dto: CreateLegPricingDto = {
          legId,
          providerId: form.providerId,
          ratePerKg,
          ratePerCbm,
          flatAmount,
          currency: form.currency,
          minimumCharge: minCharge,
          estimatedDays: days,
          notes: form.notes || null,
        };
        await freightLegPricingsService.create(dto);
        toast({ title: 'Precio agregado', variant: 'success' });
      }
      cancelForm();
      await loadData();
      onSaved?.();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.message ?? 'No se pudo guardar',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p: FreightRouteLegProviderPricing) => {
    const providerName = p.provider?.name ?? 'este proveedor';
    if (
      !(await confirmar({
        title: `¿Eliminar el precio activo de ${providerName}? El histórico queda preservado.`,
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    )
      return;
    try {
      await freightLegPricingsService.remove(p.id);
      toast({ title: 'Precio archivado', variant: 'success' });
      await loadData();
      onSaved?.();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.message ?? 'No se pudo eliminar',
        variant: 'destructive',
      });
    }
  };

  // ── Render helpers ─────────────────────────────────────────────
  const usedProviderIds = new Set((bundle?.active ?? []).map((p) => p.providerId));
  const availableProviders = editingId
    ? providers
    : providers.filter((p) => !usedProviderIds.has(p.id));

  const historyByProvider = (providerId: string) =>
    (bundle?.history ?? []).filter((h) => h.providerId === providerId);

  const ModeIcon = bundle?.leg.transportMode ? MODE_ICON[bundle.leg.transportMode] : null;

  // ── UI ─────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span>Precios por proveedor</span>
            {bundle && (
              <>
                <Badge variant="outline" className="gap-1">
                  {ModeIcon && <ModeIcon className="w-3 h-3" />}
                  {MODE_LABEL[bundle.leg.transportMode] ?? bundle.leg.transportMode}
                </Badge>
                <Badge variant="secondary">
                  {LEG_TYPE_LABEL[bundle.leg.legType] ?? bundle.leg.legType}
                </Badge>
              </>
            )}
          </DialogTitle>
          {bundle && (
            <DialogDescription>
              <span className="font-medium text-foreground">{bundle.leg.origin}</span>
              {' → '}
              <span className="font-medium text-foreground">{bundle.leg.destination}</span>
              {' · '}Configurá una forma de cobro y precio por proveedor. Los cambios se versionan
              automáticamente.
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* Lista de precios activos */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : !bundle || bundle.active.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed rounded-lg">
              <p className="text-sm text-muted-foreground mb-3">
                Aún no hay proveedores cotizando este tramo.
              </p>
              {!showAddForm && (
                <Button onClick={startAdd} disabled={availableProviders.length === 0}>
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar primer proveedor
                </Button>
              )}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="grid grid-cols-[1.2fr_2.2fr_0.9fr_0.5fr_0.8fr_auto] gap-2 px-3 py-2 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <div>Proveedor</div>
                <div>Tarifas</div>
                <div>Mínimo</div>
                <div className="text-center">Días</div>
                <div>Vigente desde</div>
                <div className="text-right pr-1">Acciones</div>
              </div>
              {bundle.active.map((p) => {
                const hist = historyByProvider(p.providerId);
                const isExpanded = expandedProviderId === p.providerId;

                return (
                  <div key={p.id} className={cn('border-t', editingId === p.id && 'bg-amber-50')}>
                    <div className="grid grid-cols-[1.2fr_2.2fr_0.9fr_0.5fr_0.8fr_auto] gap-2 px-3 py-3 items-center text-sm">
                      <div className="font-medium">{p.provider?.name ?? '—'}</div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                        {p.ratePerKg != null && (
                          <span className="inline-flex items-center gap-1">
                            <Weight className="w-3 h-3 text-muted-foreground" />
                            <span className="font-mono">
                              {p.currency} {asNumber(p.ratePerKg).toFixed(2)}
                            </span>
                            <span className="text-muted-foreground">/kg</span>
                          </span>
                        )}
                        {p.ratePerCbm != null && (
                          <span className="inline-flex items-center gap-1">
                            <Box className="w-3 h-3 text-muted-foreground" />
                            <span className="font-mono">
                              {p.currency} {asNumber(p.ratePerCbm).toFixed(2)}
                            </span>
                            <span className="text-muted-foreground">/m³</span>
                          </span>
                        )}
                        {p.flatAmount != null && (
                          <span className="inline-flex items-center gap-1">
                            <DollarSign className="w-3 h-3 text-muted-foreground" />
                            <span className="font-mono">
                              {p.currency} {asNumber(p.flatAmount).toFixed(2)}
                            </span>
                            <span className="text-muted-foreground">fijo</span>
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-muted-foreground text-xs">
                        {p.minimumCharge != null
                          ? `${p.currency} ${asNumber(p.minimumCharge).toFixed(2)}`
                          : '—'}
                      </div>
                      <div className="text-center text-muted-foreground text-xs">
                        {p.estimatedDays != null ? `${p.estimatedDays}d` : '—'}
                      </div>
                      <div className="text-xs text-muted-foreground">{fmt(p.validFrom)}</div>
                      <div className="flex justify-end gap-0.5">
                        {hist.length > 0 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setExpandedProviderId(isExpanded ? null : p.providerId)}
                            title={`Ver historial (${hist.length})`}
                          >
                            <History className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => startEdit(p)}
                          title="Editar"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleDelete(p)}
                          title="Archivar"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>

                    {p.notes && (
                      <div className="px-3 pb-2 text-xs text-muted-foreground italic">
                        Nota: {p.notes}
                      </div>
                    )}

                    {/* Historial colapsable */}
                    {isExpanded && hist.length > 0 && (
                      <div className="bg-muted/50 px-3 py-2 border-t">
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
                          <History className="w-3 h-3" />
                          Historial de {p.provider?.name}
                        </div>
                        <div className="space-y-1">
                          {hist.map((h) => (
                            <div
                              key={h.id}
                              className="grid grid-cols-[1.2fr_2.5fr_1fr] gap-2 text-xs items-center py-1"
                            >
                              <div className="text-muted-foreground">
                                {fmt(h.validFrom)} → {fmt(h.validUntil)}
                              </div>
                              <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                                {h.ratePerKg != null && (
                                  <span className="font-mono">
                                    {h.currency} {asNumber(h.ratePerKg).toFixed(2)}/kg
                                  </span>
                                )}
                                {h.ratePerCbm != null && (
                                  <span className="font-mono">
                                    {h.currency} {asNumber(h.ratePerCbm).toFixed(2)}/m³
                                  </span>
                                )}
                                {h.flatAmount != null && (
                                  <span className="font-mono">
                                    {h.currency} {asNumber(h.flatAmount).toFixed(2)} fijo
                                  </span>
                                )}
                              </div>
                              <div className="font-mono text-muted-foreground">
                                {h.minimumCharge != null
                                  ? `mín ${h.currency} ${asNumber(h.minimumCharge).toFixed(2)}`
                                  : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Botón "Agregar proveedor" */}
          {bundle && bundle.active.length > 0 && !showAddForm && !editingId && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={startAdd}
                disabled={availableProviders.length === 0}
                className="gap-1"
              >
                <Plus className="w-4 h-4" />
                {availableProviders.length === 0
                  ? 'Todos los proveedores ya tienen precio'
                  : 'Agregar otro proveedor'}
              </Button>
            </div>
          )}

          {/* Form de agregar / editar */}
          {(showAddForm || editingId) && (
            <div
              className={cn(
                'border rounded-lg p-4 space-y-4',
                editingId ? 'bg-amber-50 border-amber-300' : 'bg-muted/30'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold">
                  {editingId ? '✏️ Editar precio (creará nueva versión)' : '➕ Nuevo precio'}
                </div>
                <Button variant="ghost" size="sm" onClick={cancelForm}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Proveedor</Label>
                  <SearchableSelect
                    value={form.providerId}
                    onValueChange={(v) => setForm((f) => ({ ...f, providerId: v }))}
                    placeholder="Seleccionar proveedor..."
                    searchPlaceholder="Buscar proveedor..."
                    emptyMessage="No hay proveedores disponibles"
                    disabled={!!editingId}
                    options={availableProviders.map((p) => ({ value: p.id, label: p.name }))}
                  />
                </div>

                <div>
                  <Label>Moneda</Label>
                  <SearchableSelect
                    value={form.currency}
                    onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}
                    placeholder="Seleccionar moneda..."
                    searchPlaceholder="Buscar moneda..."
                    options={
                      currencies.length > 0
                        ? currencies.map((c) => ({ value: c.code, label: `${c.code} · ${c.name}` }))
                        : FALLBACK_CURRENCIES.map((c) => ({ value: c, label: c }))
                    }
                  />
                </div>
              </div>

              {/* Tres inputs — el usuario carga todas las formas que el
                  proveedor ofrece. Al cotizar el tramo, se elige cuál aplicar. */}
              <div>
                <Label className="mb-2 block">
                  Tarifas del proveedor{' '}
                  <span className="text-muted-foreground text-xs font-normal">
                    (cargá todas las que cotice — al menos una)
                  </span>
                </Label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Weight className="w-3.5 h-3.5" />
                      <span>Por Kg ({form.currency}/kg)</span>
                    </div>
                    <Input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={form.ratePerKg}
                      onChange={(e) => setForm((f) => ({ ...f, ratePerKg: e.target.value }))}
                      placeholder="0.00"
                      autoFocus={!editingId}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <Box className="w-3.5 h-3.5" />
                      <span>Por m³ ({form.currency}/m³)</span>
                    </div>
                    <Input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={form.ratePerCbm}
                      onChange={(e) => setForm((f) => ({ ...f, ratePerCbm: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                      <DollarSign className="w-3.5 h-3.5" />
                      <span>Monto fijo ({form.currency})</span>
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.flatAmount}
                      onChange={(e) => setForm((f) => ({ ...f, flatAmount: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Cargo mínimo ({form.currency})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.minimumCharge}
                    onChange={(e) => setForm((f) => ({ ...f, minimumCharge: e.target.value }))}
                    placeholder="Sin mínimo"
                  />
                </div>
                <div>
                  <Label>Días de tránsito</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.estimatedDays}
                    onChange={(e) => setForm((f) => ({ ...f, estimatedDays: e.target.value }))}
                    placeholder="—"
                  />
                </div>
                <div className="col-span-1" />
              </div>

              <div>
                <Label>Notas (opcional)</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Vigencia, condiciones, observaciones..."
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={cancelForm}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={
                    saving ||
                    !form.providerId ||
                    (!form.ratePerKg && !form.ratePerCbm && !form.flatAmount)
                  }
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {editingId ? 'Guardar nueva versión' : 'Crear precio'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
